<template>
  <InfuseModal :title="t('title')" @close="emit('close')">
    <form :id="`edit-${variable.name}`" @submit.prevent="save">
      <InfuseField v-if="variable.dataType === DataType.INTEGER" :label="variable.name">
        <InfuseInput
          v-model="variableValue"
          :max="variable.maxLimit"
          :min="variable.minLimit"
          ref="inputRef"
          type="number"
        />
      </InfuseField>
      <InfuseField
        v-else-if="variable.dataType === DataType.DECIMAL"
        :help-text="t('help-text.decimal')"
        :label="variable.name"
      >
        <InfuseInput
          v-model="variableValue"
          :min="variable.minLimit"
          :max="variable.maxLimit"
          :step="0.000001"
          pattern="^\d*(,\d{0,6})?$"
          ref="inputRef"
          type="number"
        />
      </InfuseField>
      <InfuseField v-else-if="variable.dataType === DataType.BOOLEAN" :label="variable.name">
        <InfuseToggle
          :id="variable.name"
          :value="variableValue.toLowerCase() === 'true'"
          @off="variableValue = 'false'"
          @on="variableValue = 'true'"
        />
      </InfuseField>
      <InfuseField
        v-else-if="
          (variable.dataType === DataType.MEMBER_LIST || variable.dataType === DataType.SEQUENCE_LIST) &&
          !variable.enumValues
        "
        :help-text="
          variable.dataType === DataType.MEMBER_LIST ? t('help-text.memberList') : t('help-text.sequenceList')
        "
        :label="variable.name"
      >
        <InfuseTextArea v-model="variableValue" :rows="4" size="medium" ref="inputRef" />
      </InfuseField>
      <InfuseField
        v-else-if="
          (variable.dataType === DataType.MEMBER_LIST || variable.dataType === DataType.SEQUENCE_LIST) &&
          variable.enumValues
        "
        :help-text="t('help-text.multiSelect')"
        :label="variable.name"
      >
        <InfuseMultiSelect
          :closeOnSelect="false"
          :modelValue="variableValue.split(',')"
          :name="variable.name"
          :options="variable.enumValues.split(',')"
          mode="tags"
          @update:modelValue="(value: string[]) => (variableValue = value.join(','))"
        />
      </InfuseField>
      <InfuseField v-else-if="variable.dataType === DataType.OPTION_LIST && variable.enumValues" :label="variable.name">
        <InfuseSelect
          v-model="variableValue"
          :name="variable.name"
          :options="variable.enumValues.split(',')"
          ref="inputRef"
        />
      </InfuseField>
      <InfuseField
        v-else-if="variable.dataType === DataType.DATE_TIME"
        :help-text="t('help-text.dateTime')"
        :label="variable.name"
      >
        <InfuseDatePicker v-model="variableValueAsDate" utc="preserve" />
      </InfuseField>
      <!-- DataType.STRING -->
      <InfuseField v-else :label="variable.name">
        <InfuseInput v-model="variableValue" ref="inputRef" />
      </InfuseField>
    </form>

    <template #actions>
      <InfuseButton
        data-testid="modal-confirm-button"
        type="submit"
        :form="`edit-${variable.name}`"
        :disabled="loading"
        :loading="loading"
        >{{ t('save') }}</InfuseButton
      >
    </template>
  </InfuseModal>
</template>

<script lang="ts" setup>
import { AttributeType, DataType, Variable } from '@infuse/cpms/charging-station/store';

import {
  InfuseButton,
  InfuseDatePicker,
  InfuseField,
  InfuseInput,
  InfuseModal,
  InfuseMultiSelect,
  InfuseSelect,
  InfuseTextArea,
  InfuseToggle
} from '@infuse/ui/components';
import { onMounted, Ref, ref } from 'vue';
import { useI18n } from 'vue-i18n';

interface Props {
  variable: Variable;
  attributeType: AttributeType;
  loading?: boolean;
}
interface Emits {
  (event: 'close'): void;
  (event: 'save', variable: Variable, attributeType: AttributeType, newValue: string): void;
}

const props = withDefaults(defineProps<Props>(), {
  loading: false
});
const emit = defineEmits<Emits>();

const { t } = useI18n();

const inputRef = ref<InstanceType<typeof InfuseInput>>();
const variableValue: Ref<string> = ref('');
const variableValueAsDate: Ref<Date> = ref(new Date());

function save() {
  let newValue = variableValue.value;
  if (props.variable.dataType === DataType.DATE_TIME) {
    newValue = variableValueAsDate.value.toISOString();
  }

  emit('save', props.variable, props.attributeType, newValue);
}

onMounted(() => {
  inputRef.value?.focus();
  const attribute = props.variable.attributes.find((a) => a.type === props.attributeType);
  if (!attribute) {
    throw new Error('No attribute found for configuration');
  }

  variableValue.value = attribute.value || '';
  if (props.variable.dataType === DataType.DATE_TIME) {
    variableValueAsDate.value = new Date(variableValue.value);
  }
});
</script>

<i18n>
{
  "en": {
    "title": "Edit configuration",
    "value": "Value",
    "save": "Save",
    "help-text": {
      "decimal": "Shall not exceed 6 decimal places",
      "memberList": "Unordered comma-separated list",
      "sequenceList": "Ordered comma-separated list, starting with the most important value",
      "multiSelect": "Multiple values can be selected",
      "dateTime": "Date and time are in UTC"
    }
  },
  "nl": {
    "title": "Wijzig configuratie",
    "value": "Waarde",
    "save": "Opslaan",
    "help-text": {
      "decimal": "Mag niet meer dan 6 decimalen bedragen",
      "memberList": "Ongeordende, door komma's gescheiden lijst",
      "sequenceList": "Geordende, door komma's gescheiden lijst, beginnend met de belangrijkste waarde",
      "multiSelect": "Meerdere waardes kunnen worden geselecteerd",
      "dateTime": "Datum en tijd zijn in UTC"
    }
  }
}
</i18n>
